import { Button, DialogTrigger, makeStyles, mergeClasses, shorthands, Spinner } from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import { Dismiss20Filled, DocumentAdd20Regular, FolderArrowRight20Regular } from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFeatureEnabled } from '../../../api/features/features';
import { useDeleteReturnsFromFolder, useGetAllFolders } from '../../../api/report/report';
import { ISelectedTaxReturnsAndFilters } from '../../../data-types/exportReports';
import { IFilterItem } from '../../../data-types/filterItem';
import { FilterTabDefinition } from '../../../data-types/filterTabDefinition';
import { useLegacyReportsTableFiltering } from '../../../hooks/reportsTable/useLegacyReportsTableFiltering';
import { useFeedbackMessages } from '../../../hooks/useFeedbackMessages';
import { useFunctionalAccess } from '../../../hooks/useFunctionalAccess';
import { useGridState } from '../../../hooks/useGridState';
import { useSearchTable } from '../../../hooks/useSearchTable';
import { customPalette } from '../../../theme';
import CartButton from '../../common/CartButton';
import ExportReportButton from '../../common/ExportReportButton';
import FilterGroup from '../../common/FilterGroup';
import FunctionalAccessDisableWrapper from '../../common/FunctionalAccessDisableWrapper';
import GridSearchBar from '../../common/GridSearchBar';
import ConfirmationDialogV9 from '../../dialog/ConfirmationDialogV9';
import FilterButton from '../../filters/filter-button/FilterButton';
import TabFilterButton from '../../filters/tab-filter-button/TabFilterButton';
import AddToFolderModal from '../../folders/AddToFolderModal';
import HelpButton from '../../help/HelpButton';
import FoldersListFilter from './FoldersListFilter';

export interface IFilterBarProps {
    isFiltered: boolean;
    periods: IFilterItem[];
    entities: IFilterItem[];
    entityGroups: IFilterItem[];
    cases: IFilterItem[];
    jurisdictions: IFilterItem[];
    folders: IFilterItem[];
    disabled: boolean;
    clearHighlightedElements: () => void;
    updateFolderList?: () => Promise<any>;
    getReportsToExport: () => ISelectedTaxReturnsAndFilters;
    disableActionButtons?: boolean;
    openAddReturnSidebarHandler?: () => void;
}

const useStyles = makeStyles({
    breakStack: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        width: 'auto',
        height: '15px',
        flex: 'none',
        order: 0,
        flexGrow: 0,
        zIndex: 0,
        opacity: '100%',
        backgroundColor: 'transparent',
    },
    menuBarBase: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'self-end',
        paddingTop: '12px',
        paddingRight: '8px',
        paddingBottom: '12px',
        paddingLeft: '8px',
        width: 'auto',
        flex: 'none',
        order: 0,
        flexGrow: 0,
        zIndex: 0,
        ...shorthands.border('0', 'none', 'transparent'),
        boxShadow: '0px 1.6px 0px 0px #00000021',
        backgroundColor: '#fff',
        gap: '12px 15px',
    },
    headerBar: {
        paddingTop: '12px',
        paddingRight: '16px',
        paddingBottom: '12px',
        paddingLeft: '16px',
        justifyContent: 'space-between',
    },
    filterBar: {
        position: 'relative',
        marginTop: 0,
        marginRight: '15px',
        marginBottom: 0,
        marginLeft: '15px',
        ...shorthands.border('1px', 'solid', customPalette.greyD1D3D4),
        borderTopLeftRadius: '6px',
        borderTopRightRadius: '6px',
        borderBottomRightRadius: 0,
        borderBottomLeftRadius: 0,
        boxShadow: 'none',
    },
    filterPanel: {
        position: 'relative',
    },
    rightAlignButtons: {
        display: 'flex',
        flexDirection: 'row',
    },
    leftAlignButtons: {
        display: 'flex',
        flexDirection: 'row',
    },
    // the "Select Folder" button has a label on top of it. When that whole component is put into
    // a flexbox, the other items want to vertically align with that label, not the button.
    // setting align-items: self-end fixes this by pulling all the button elements towards the bottom
    // when rendered inside a row-based flexbox layout
    folderFilterWrapper: {
        alignItems: 'self-end',
        gap: '15px',
    },
    cartAndMiscActions: {
        gap: '12px',
    },
    addNewReturnButton: {
        marginLeft: 'auto',
    },
});

const FilterBar: React.FC<IFilterBarProps> = ({
    isFiltered,
    periods,
    entities,
    entityGroups,
    cases,
    jurisdictions,
    folders,
    disabled,
    getReportsToExport,
    clearHighlightedElements,
    updateFolderList,
    disableActionButtons = false,
    openAddReturnSidebarHandler,
}) => {
    const { t } = useTranslation();
    const styles = useStyles();
    const { displayFeedbackMessage } = useFeedbackMessages();
    const { isSearchValueApplied, clearSearch } = useSearchTable();
    const { grid } = useGridState();
    const queryClient = useQueryClient();
    const [isFolderModalVisible, { toggle: toggleFolderModalVisibility }] = useBoolean(false);
    const [removeFromFolderConfirmationVisible, { setTrue: showRemoveFromFolderConfirmation, setFalse: hideRemoveFromFolderConfirmation }] =
        useBoolean(false);
    const { functionalAccess } = useFunctionalAccess();
    const selectedReturns = getReportsToExport().selectedTaxReturns?.map((r) => r.id ?? 0) || [];
    const {
        folderFilterName,
        entityFilterName,
        jurisdictionFilterName,
        periodFilterName,
        caseFilterName,
        clearAllFilters,
        checkedPeriodItems,
        setCheckedPeriodItems,
        checkedEntityItems,
        setCheckedEntityItems,
        checkedEntityGroupItems,
        setCheckedEntityGroupItems,
        checkedCaseItems,
        setCheckedCaseItems,
        checkedJurisdictionItems,
        setCheckedJurisdictionItems,
        selectedFolderFilter,
        setSelectedFolderFilter,
        searchEntityValue,
        setSearchEntityValue,
        searchJurisdictionValue,
        setSearchJurisdictionValue,
        searchPeriodValue,
        setSearchPeriodValue,
        searchCaseValue,
        setSearchCaseValue,
    } = useLegacyReportsTableFiltering();
    const { data: folderItems, isLoading: isLoadingGetAllFolders, queryKey: getAllFoldersQuery } = useGetAllFolders();
    const { data: isCreateNewReturnEnabled } = useIsFeatureEnabled({
        featureFlagName: 'CreateNewReturnEnabled',
    });

    const { mutateAsync: deleteReturnsFromFolder, isLoading: isLoadingDeleteReturnsFromFolder } = useDeleteReturnsFromFolder({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries(getAllFoldersQuery);
                hideRemoveFromFolderConfirmation();
                displayFeedbackMessage({ type: 'success', message: t('calculationRemovedSuccessMessage') });
            },
        },
    });

    const entityFilterTabs = useMemo((): FilterTabDefinition[] => {
        return [
            {
                label: t('entity'),
                items: entities,
                checkedItems: checkedEntityItems,
                isSingleSelect: false,
                noElementsFoundtext: t('noEntitiesFound'),
                setCheckedItems: setCheckedEntityItems,
            },
            {
                label: t('entityGroups'),
                items: entityGroups,
                checkedItems: checkedEntityGroupItems,
                isSingleSelect: true,
                noElementsFoundtext: t('noEntityGroupsFound'),
                setCheckedItems: setCheckedEntityGroupItems,
            },
        ];
    }, [t, entities, checkedEntityItems, entityGroups, checkedEntityGroupItems]);

    const isFilteredByFolder = useMemo(() => {
        return !!selectedFolderFilter;
    }, [selectedFolderFilter]);

    const clearAllFiltersAndSearch = () => {
        clearHighlightedElements();
        clearAllFilters();
        clearSearch(grid);
    };

    const onFolderChange = useCallback(
        (filteredItems: IFilterItem[], _filterName: string) => {
            setSelectedFolderFilter(filteredItems[0]);
        },
        [setSelectedFolderFilter]
    );

    const removeSelectedReturnsFromFolder = async () => {
        const selectedReturns = getReportsToExport().selectedTaxReturns ?? [];
        const selectedIds = selectedReturns.map((r) => r.id ?? 0);

        await deleteReturnsFromFolder({
            data: {
                folderKey: parseInt(selectedFolderFilter?.key ?? '0'),
                keys: selectedIds,
            },
        });

        deleteRecordsFromGridById(selectedIds);
    };

    const deleteRecordsFromGridById = (ids: number[] | null | undefined) => {
        if (grid && grid.current && ids) {
            ids.forEach((id) => {
                grid.current?.deleteRecord('id', id);
            });
        }
    };

    return (
        <div className={styles.filterPanel}>
            <div className={mergeClasses(styles.menuBarBase, styles.headerBar)}>
                <div className={mergeClasses(styles.leftAlignButtons, styles.folderFilterWrapper)}>
                    <FunctionalAccessDisableWrapper hasFunctionalAccess={functionalAccess?.folderDropdownEnabled}>
                        <FoldersListFilter
                            folders={folders}
                            isFiltered={isFilteredByFolder}
                            filterName={folderFilterName}
                            label='allCalculations'
                            selectedFolderFilter={selectedFolderFilter}
                            showSingleSelect={true}
                            onFolderChange={onFolderChange}
                            disabled={disabled}
                            updateFolderList={updateFolderList}
                        />
                    </FunctionalAccessDisableWrapper>

                    <FunctionalAccessDisableWrapper
                        hasFunctionalAccess={functionalAccess?.folderDropdownEnabled && functionalAccess?.editFolderButtonEnabled}
                    >
                        <Button
                            aria-label={t('addToFolder').toString()}
                            icon={<FolderArrowRight20Regular />}
                            onClick={toggleFolderModalVisibility}
                            disabled={disabled || disableActionButtons}
                            disabledFocusable={disabled || disableActionButtons}
                        >
                            {t('addToFolder').toString()}
                        </Button>
                    </FunctionalAccessDisableWrapper>
                    {isFilteredByFolder && (
                        <FunctionalAccessDisableWrapper hasFunctionalAccess={functionalAccess?.editFolderButtonEnabled}>
                            <Button
                                aria-label={t('removeFromFolder').toString()}
                                icon={<Dismiss20Filled />}
                                onClick={showRemoveFromFolderConfirmation}
                                disabled={disabled || disableActionButtons}
                                disabledFocusable={disabled || disableActionButtons}
                            >
                                {t('removeFromFolder').toString()}
                            </Button>
                        </FunctionalAccessDisableWrapper>
                    )}
                </div>
                <div className={mergeClasses(styles.rightAlignButtons, styles.cartAndMiscActions)}>
                    <CartButton getReportsToExport={getReportsToExport} disabled={disabled || disableActionButtons} />
                    <ExportReportButton
                        getReportsToExport={getReportsToExport}
                        disabled={disabled || disableActionButtons}
                        includeExportList={true}
                    ></ExportReportButton>
                    <HelpButton />
                </div>
            </div>
            <div className={styles.breakStack}></div>
            <div className={mergeClasses(styles.menuBarBase, styles.filterBar)}>
                <FilterGroup
                    clearAllFiltersLabel={t('filterBarClearAllFilters')}
                    isFiltered={isFiltered || isSearchValueApplied}
                    clearAllFilters={clearAllFiltersAndSearch}
                >
                    <TabFilterButton
                        filterName={entityFilterName}
                        tabs={entityFilterTabs}
                        label='entityOrEntityGroups'
                        searchValue={searchEntityValue}
                        setSearchValue={setSearchEntityValue}
                        disabled={disabled}
                    />
                    <FilterButton
                        filterName={jurisdictionFilterName}
                        items={jurisdictions}
                        checkedItems={checkedJurisdictionItems}
                        setCheckedItems={setCheckedJurisdictionItems}
                        searchValue={searchJurisdictionValue}
                        setSearchValue={setSearchJurisdictionValue}
                        noElementsFoundtext={t('noJurisdictionsFound')}
                        disabled={disabled}
                    />
                    <FilterButton
                        filterName={periodFilterName}
                        items={periods}
                        checkedItems={checkedPeriodItems}
                        setCheckedItems={setCheckedPeriodItems}
                        searchValue={searchPeriodValue}
                        setSearchValue={setSearchPeriodValue}
                        noElementsFoundtext={t('noPeriodsFound')}
                        disabled={disabled}
                    />
                    <FilterButton
                        filterName={caseFilterName}
                        items={cases}
                        checkedItems={checkedCaseItems}
                        setCheckedItems={setCheckedCaseItems}
                        searchValue={searchCaseValue}
                        setSearchValue={setSearchCaseValue}
                        noElementsFoundtext={t('noCasesFound')}
                        disabled={disabled}
                    />
                    <GridSearchBar searchBoxPlaceholderText={t('search')} disabled={disabled} />
                </FilterGroup>
                {isCreateNewReturnEnabled && (
                    <div className={styles.addNewReturnButton}>
                        <FunctionalAccessDisableWrapper hasFunctionalAccess={functionalAccess?.createReportsButtonEnabled}>
                            <Button
                                appearance='primary'
                                aria-label={t('addNew').toString()}
                                data-analytics-button-name='add_new_calculation_button'
                                onClick={() => {
                                    openAddReturnSidebarHandler && openAddReturnSidebarHandler();
                                }}
                                role='button'
                                id='addNewReturnButton'
                                className={styles.addNewReturnButton}
                                icon={<DocumentAdd20Regular />}
                                disabled={disabled}
                                disabledFocusable={disabled}
                            >
                                {t('addNew').toString()}
                            </Button>
                        </FunctionalAccessDisableWrapper>
                    </div>
                )}
            </div>
            <AddToFolderModal
                isOpen={isFolderModalVisible}
                folders={folderItems ?? []}
                selectedReturns={selectedReturns}
                isLoadingGetAllFolders={isLoadingGetAllFolders}
                getAllFoldersQuery={getAllFoldersQuery}
                onClose={toggleFolderModalVisibility}
            ></AddToFolderModal>
            {removeFromFolderConfirmationVisible && (
                <ConfirmationDialogV9
                    onDismiss={hideRemoveFromFolderConfirmation}
                    content={t('removeFromFolderDialogContent')}
                    title={t('removeFromFolderDialogHeading')}
                    buttons={
                        <>
                            <DialogTrigger>
                                <Button
                                    aria-label={t('cancel').toString()}
                                    onClick={hideRemoveFromFolderConfirmation}
                                    disabled={isLoadingDeleteReturnsFromFolder}
                                    data-analytics-button-name='confirmation_dialog_cancel'
                                >
                                    {t('cancel').toString()}
                                </Button>
                            </DialogTrigger>
                            <Button
                                appearance='primary'
                                aria-label={t('remove').toString()}
                                onClick={removeSelectedReturnsFromFolder}
                                disabled={Boolean(isLoadingDeleteReturnsFromFolder)}
                                data-analytics-button-name='confirmation_dialog_save'
                            >
                                {isLoadingDeleteReturnsFromFolder ? t('removing').toString() : t('remove').toString()}
                                {isLoadingDeleteReturnsFromFolder && <Spinner size='extra-tiny' />}
                            </Button>
                        </>
                    }
                />
            )}
        </div>
    );
};

export default FilterBar;
